import type { ContractDeploymentRecord, ContractRegistryRecord } from '@/types'

export type DeploymentDisplayAddressKind =
  | 'payment'
  | 'contract'
  | 'predicted_contract'
  | 'deployed_contract'
  | 'vault_token_account'

export type DeploymentDisplayAddress = {
  kind: DeploymentDisplayAddressKind
  address: string
  isMismatch?: boolean
}

const normalizeAddress = (value?: string | null): string | null => {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

const addressesMatch = (
  left: string,
  right: string,
  compatibility?: string
): boolean => compatibility?.toLowerCase() === 'evm'
  ? left.toLowerCase() === right.toLowerCase()
  : left === right

export const getDeploymentTablePaymentAddress = (
  record: ContractDeploymentRecord
): string | null => normalizeAddress(record.payment_address)

export const getDeploymentDisplayAddresses = (
  record: ContractDeploymentRecord
): DeploymentDisplayAddress[] => {
  const compatibility = record.chain_compatibility?.trim().toLowerCase()
  const paymentAddress = normalizeAddress(record.payment_address)
  const operationalAddress = normalizeAddress(record.operational_address)

  if (compatibility === 'solana') {
    return [
      ...(paymentAddress ? [{ kind: 'payment' as const, address: paymentAddress }] : []),
      ...(operationalAddress
        ? [{ kind: 'vault_token_account' as const, address: operationalAddress }]
        : []),
    ]
  }

  if (
    paymentAddress
    && operationalAddress
    && !addressesMatch(paymentAddress, operationalAddress, compatibility)
  ) {
    return [
      { kind: 'predicted_contract', address: paymentAddress, isMismatch: true },
      { kind: 'deployed_contract', address: operationalAddress, isMismatch: true },
    ]
  }

  const contractAddress = operationalAddress ?? paymentAddress
  return contractAddress ? [{ kind: 'contract', address: contractAddress }] : []
}

export const getRegistryActionDisplayAddresses = (
  record: ContractRegistryRecord
): DeploymentDisplayAddress[] => {
  const compatibility = record.chain_compatibility?.trim().toLowerCase()
  const paymentAddress = normalizeAddress(record.payment_address)
  const predictedOperationalAddress = normalizeAddress(record.predicted_contract_address)

  if (compatibility === 'solana') {
    return paymentAddress ? [{ kind: 'payment', address: paymentAddress }] : []
  }

  if (
    paymentAddress
    && predictedOperationalAddress
    && !addressesMatch(paymentAddress, predictedOperationalAddress, compatibility)
  ) {
    return [
      { kind: 'payment', address: paymentAddress, isMismatch: true },
      {
        kind: 'predicted_contract',
        address: predictedOperationalAddress,
        isMismatch: true,
      },
    ]
  }

  if (paymentAddress && predictedOperationalAddress) {
    return [{ kind: 'contract', address: paymentAddress }]
  }

  if (paymentAddress) {
    return [{ kind: 'payment', address: paymentAddress }]
  }

  return predictedOperationalAddress
    ? [{ kind: 'predicted_contract', address: predictedOperationalAddress }]
    : []
}
